// SCR-STAFF-201 تب «اطلاعات عمومی» (AP-CAT-02): نام و توضیح عمومی، تصویر، آمادگی (متغیر {prep_instructions} یادآوری — AC-CAT-006.2)،
// مراقبت پس از خدمت، نمایش قیمت و کانال‌های رزرو (US-CAT-006، AC-CAT-006.1)؛ فاصله پیشنهادی نوبت بعد (US-CAT-015).
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type CatalogBookingChannel,
  type ServiceSetupView,
  type ServiceView,
} from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  ErrorState,
  LoadingState,
  SelectRHF,
  TextFieldRHF,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { BOOKING_CHANNELS, catKeys, SavedNotice } from './common';

const publicSchema = z.object({
  publicName: z.string().max(200, { error: 'errors:validation.max_length' }),
  publicDescription: z.string().max(2000, { error: 'errors:validation.max_length' }),
  imageUrl: z
    .string()
    .trim()
    .refine((value) => value === '' || /^https:\/\/\S+$/i.test(value), { message: 'errors:validation.url' }),
  prepInstructions: z.string().max(120, { error: 'errors:validation.max_length' }),
  aftercareInstructions: z.string().max(2000, { error: 'errors:validation.max_length' }),
  priceDisplay: z.enum(['Show', 'From', 'Hide']),
  channels: z.array(z.enum(BOOKING_CHANNELS)),
  isPublic: z.boolean(),
  isVirtual: z.boolean(),
});
type PublicForm = z.input<typeof publicSchema>;

const followUpSchema = z.object({ minDays: z.string(), maxDays: z.string() }).superRefine((value, ctx) => {
  const min = value.minDays.trim();
  const max = value.maxDays.trim();
  if (!min && !max) {
    return;
  }
  const minDays = Number(min);
  const maxDays = Number(max);
  if (
    !min ||
    !max ||
    !Number.isInteger(minDays) ||
    !Number.isInteger(maxDays) ||
    minDays < 1 ||
    maxDays > 730 ||
    minDays > maxDays
  ) {
    ctx.addIssue({ code: 'custom', path: ['maxDays'], message: 'errors:validation.follow_up_interval' });
  }
});
type FollowUpForm = z.input<typeof followUpSchema>;

const empty = (value: string) => (value.trim() ? value.trim() : null);

function PublicSection({ service, setup }: { service: ServiceView; setup: ServiceSetupView }) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cat.service.manage');
  const [saved, setSaved] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();
  const info = setup.publicInfo;

  const values = useMemo<PublicForm>(
    () => ({
      publicName: info.publicName ?? '',
      publicDescription: info.publicDescription ?? '',
      imageUrl: info.imageUrl ?? '',
      prepInstructions: info.prepInstructions ?? '',
      aftercareInstructions: info.aftercareInstructions ?? '',
      priceDisplay: info.priceDisplay,
      channels: info.channels.filter(
        (channel): channel is (typeof BOOKING_CHANNELS)[number] => channel !== 'Staff',
      ),
      isPublic: info.isPublic,
      isVirtual: info.isVirtual,
    }),
    [info],
  );
  const { control, handleSubmit, formState, setError } = useForm<PublicForm>({
    resolver: zodResolver(publicSchema),
    values,
  });

  const save = useMutation({
    mutationFn: (form: PublicForm) =>
      unwrap(
        tenantApi.PUT('/api/v1/catalog/services/{id}/public-info', {
          params: { path: { id: service.id } },
          body: {
            publicName: empty(form.publicName),
            publicDescription: empty(form.publicDescription),
            imageUrl: empty(form.imageUrl),
            prepInstructions: empty(form.prepInstructions),
            aftercareInstructions: empty(form.aftercareInstructions),
            priceDisplay: form.priceDisplay,
            channels: form.channels as CatalogBookingChannel[],
            isPublic: form.isPublic,
            isVirtual: form.isVirtual,
          },
        }),
      ),
    onMutate: () => {
      setSaved(false);
      setSubmitError(undefined);
    },
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['publicName', 'publicDescription', 'imageUrl', 'prepInstructions', 'aftercareInstructions'],
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        component="form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (write.canWrite) {
            void handleSubmit((form) => save.mutate(form))();
          }
        }}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle1">{t('cat:public.title')}</Typography>
          <WriteNotice write={write} />
          <SavedNotice show={saved && !formState.isDirty} />
          <TextFieldRHF
            control={control}
            name="publicName"
            labelKey="cat:public.public_name"
            helperKey="cat:public.public_name_help"
            fullWidth
            disabled={!write.canWrite}
          />
          <TextFieldRHF
            control={control}
            name="publicDescription"
            labelKey="cat:public.public_description"
            multiline
            minRows={2}
            fullWidth
            disabled={!write.canWrite}
          />
          <TextFieldRHF
            control={control}
            name="imageUrl"
            labelKey="cat:public.image_url"
            fullWidth
            disabled={!write.canWrite}
            slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'public-image' } }}
          />
          <TextFieldRHF
            control={control}
            name="prepInstructions"
            labelKey="cat:public.prep"
            helperKey="cat:public.prep_help"
            fullWidth
            disabled={!write.canWrite}
            slotProps={{ htmlInput: { maxLength: 120, 'data-testid': 'public-prep' } }}
          />
          <TextFieldRHF
            control={control}
            name="aftercareInstructions"
            labelKey="cat:public.aftercare"
            multiline
            minRows={2}
            fullWidth
            disabled={!write.canWrite}
          />
          <SelectRHF
            control={control}
            name="priceDisplay"
            labelKey="cat:public.price_display"
            disabled={!write.canWrite}
            options={(['Show', 'From', 'Hide'] as const).map((value) => ({
              value,
              labelKey: `cat:public.price_displays.${value}`,
            }))}
          />
          <Controller
            control={control}
            name="channels"
            render={({ field }) => (
              <FormControl component="fieldset" disabled={!write.canWrite}>
                <FormLabel component="legend">{t('cat:public.channels')}</FormLabel>
                <FormGroup row>
                  {BOOKING_CHANNELS.map((channel) => (
                    <FormControlLabel
                      key={channel}
                      label={t(`cat:public.channel.${channel}`)}
                      control={
                        <Checkbox
                          checked={field.value.includes(channel)}
                          onChange={(event) =>
                            field.onChange(
                              event.target.checked
                                ? [...field.value, channel]
                                : field.value.filter((value) => value !== channel),
                            )
                          }
                          slotProps={{ input: { 'data-testid': `public-channel-${channel}` } as object }}
                        />
                      }
                    />
                  ))}
                </FormGroup>
                <FormHelperText>{t('cat:public.staff_always')}</FormHelperText>
              </FormControl>
            )}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <CheckboxRHF
              control={control}
              name="isPublic"
              labelKey="cat:public.is_public"
              disabled={!write.canWrite}
            />
            <CheckboxRHF
              control={control}
              name="isVirtual"
              labelKey="cat:public.is_virtual"
              disabled={!write.canWrite}
            />
          </Stack>
        </Stack>
        {submitError ? <ErrorState error={submitError} compact /> : null}
        <Button
          type="submit"
          variant="contained"
          sx={{ mt: 2 }}
          disabled={!write.canWrite || save.isPending}
          data-testid="public-submit"
        >
          {t('common:actions.save')}
        </Button>
      </Box>
    </Paper>
  );
}

function FollowUpSection({ service, setup }: { service: ServiceView; setup: ServiceSetupView }) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cat.service.manage');
  const [saved, setSaved] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();

  const values = useMemo<FollowUpForm>(
    () => ({
      minDays:
        setup.followUp.minDays === null || setup.followUp.minDays === undefined
          ? ''
          : String(setup.followUp.minDays),
      maxDays:
        setup.followUp.maxDays === null || setup.followUp.maxDays === undefined
          ? ''
          : String(setup.followUp.maxDays),
    }),
    [setup],
  );
  const { control, handleSubmit, formState, setError } = useForm<FollowUpForm>({
    resolver: zodResolver(followUpSchema),
    values,
  });

  const save = useMutation({
    mutationFn: (form: FollowUpForm) =>
      unwrap(
        tenantApi.PUT('/api/v1/catalog/services/{id}/follow-up', {
          params: { path: { id: service.id } },
          body: {
            minDays: form.minDays.trim() ? Number(form.minDays) : null,
            maxDays: form.maxDays.trim() ? Number(form.maxDays) : null,
          },
        }),
      ),
    onMutate: () => {
      setSaved(false);
      setSubmitError(undefined);
    },
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, { fields: ['minDays', 'maxDays'] });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        component="form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (write.canWrite) {
            void handleSubmit((form) => save.mutate(form))();
          }
        }}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle1">{t('cat:follow_up.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('cat:follow_up.help')}
          </Typography>
          <SavedNotice show={saved && !formState.isDirty} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextFieldRHF
              control={control}
              name="minDays"
              labelKey="cat:follow_up.min_days"
              type="number"
              disabled={!write.canWrite}
              slotProps={{ htmlInput: { min: 1, max: 730, 'data-testid': 'follow-up-min' } }}
            />
            <TextFieldRHF
              control={control}
              name="maxDays"
              labelKey="cat:follow_up.max_days"
              type="number"
              disabled={!write.canWrite}
              slotProps={{ htmlInput: { min: 1, max: 730, 'data-testid': 'follow-up-max' } }}
            />
          </Stack>
        </Stack>
        {submitError ? <ErrorState error={submitError} compact /> : null}
        <Button
          type="submit"
          variant="contained"
          sx={{ mt: 2 }}
          disabled={!write.canWrite || save.isPending}
          data-testid="follow-up-submit"
        >
          {t('common:actions.save')}
        </Button>
      </Box>
    </Paper>
  );
}

/** تب اطلاعات عمومی، کانال‌ها و فاصله جلسات. */
export function PublicTab({ service }: { service: ServiceView }) {
  const { tenantApi, session } = useTenantSession();
  const setup = useQuery({
    queryKey: catKeys.setup(session?.tenantId, service.id),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/catalog/services/{id}/setup', { params: { path: { id: service.id } } })),
  });

  if (setup.isPending) {
    return <LoadingState rows={3} />;
  }
  if (setup.error) {
    return <ErrorState error={setup.error} onRetry={() => void setup.refetch()} />;
  }

  return (
    <Stack spacing={2}>
      <PublicSection service={service} setup={setup.data} />
      <FollowUpSection service={service} setup={setup.data} />
    </Stack>
  );
}
